import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  IPriceCandidate,
  IPricePolicyTrace,
  audienceRank,
  IResolvePricesRequest,
  IResolvedPrice,
  IResolvedPriceEntry,
  NO_PRICE_CANDIDATES,
  PRICE_SOURCE_TO_PRICE_TYPE,
  PriceSourceType,
  resolvePricesKey,
  samePrice,
} from '../models/quotations.models';
import { samePrice as sameTypedPrice } from '../utils/price-decimals.util';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { QuotesService } from './quotations.services';

/**
 * The single place a screen keeps what the engine answered.
 *
 * Every surface that shows a price on an order (the compact tooltip, the
 * expanded candidates panel, the side-by-side section, the manual add form)
 * reads this store, and the store makes exactly one resolve call per page of
 * lines. Not an optimisation: two requests for the same question is how two
 * surfaces end up displaying two different prices for one product.
 *
 * A failure is a silence, not an error. `resolvePrices()` resolves an
 * unreachable engine to an empty list, and an empty map renders the tooltip
 * with only its three fixed rows.
 *
 * A tenant without the wolsal_pricing addon is that same silence, decided here
 * rather than in each surface.
 */
@Injectable({ providedIn: 'root' })
export class PriceCandidatesStore {
  private byProduct = new Map<number, IResolvedPrice>();
  private inflight: Subscription | null = null;
  /**
   * The question currently answered, so a redundant load is a no-op. Keyed by
   * `resolvePricesKey`, the same identity the service memoizes its HTTP call
   * on, so the two layers cannot disagree on what "the same question" is.
   */
  private loadedKey: string | null = null;

  constructor(
    private quotationsService: QuotesService,
    private featureFlags: FeatureFlagService
  ) {}

  /**
   * Ask the engine about a whole set of products at once.
   *
   * Callers pass the products of the page they are rendering; asking per line
   * would be N calls for one screen and, worse, N chances for the answers to
   * disagree.
   */
  load(request: IResolvePricesRequest, done?: () => void): void {
    if (!this.featureFlags.pricingEnabled) {
      // No pricing module, no engine to ask.
      this.clear();
      done?.();
      return;
    }
    if (!request?.customer_id || !(request.product_ids ?? []).length) {
      // No customer means no question to ask: a price is always a price for
      // someone. The surfaces show "choose a customer", never a price.
      this.clear();
      done?.();
      return;
    }
    const key = resolvePricesKey(request);
    if (key === this.loadedKey) {
      done?.();
      return;
    }

    this.inflight?.unsubscribe();
    this.inflight = this.quotationsService.resolvePrices(request).subscribe({
      next: (entries: IResolvedPriceEntry[]) => {
        this.byProduct.clear();
        (entries ?? []).forEach((entry) => {
          if (entry?.product_id != null) {
            this.byProduct.set(entry.product_id, entry);
          }
        });
        // The API answers one entry per requested product, so an empty list for
        // a non-empty request is a failed engine, not an answer. Leaving
        // `loadedKey` unset lets the next load re-ask instead of no-opping on a
        // memoized silence for the rest of the session.
        this.loadedKey = (entries ?? []).length ? key : null;
        done?.();
      },
      error: () => {
        // Unreachable through resolvePrices, which catches; kept so a caller
        // that bypasses it degrades the same way instead of throwing inside a
        // template.
        this.clear();
        done?.();
      },
    });
  }

  /**
   * The same question, asked from a page of lines.
   *
   * The three surfaces that price a line (the order table, the manual add form,
   * the side-by-side panel) all ask it. Building the request here means a field
   * added to `IResolvePricesRequest` reaches every surface at once.
   */
  loadForLines(
    customerId: number | null | undefined,
    productIds: (number | null)[],
    locationId: number | null = null,
    /** The order on screen — excluded from its own history. */
    excludeOrderId: number | null = null
  ): void {
    const ids = Array.from(
      new Set(productIds.filter((id): id is number => typeof id === 'number'))
    );
    if (!customerId || !ids.length) {
      // No customer means no question to ask: a price is always a price for
      // someone. The surfaces show "choose a customer", never a price.
      this.clear();
      return;
    }
    this.load({
      customer_id: customerId,
      product_ids: ids,
      location_id: locationId,
      exclude_order_ids: excludeOrderId ? [excludeOrderId] : null,
    });
  }

  /** Everything known about one product, or null when nothing was asked. */
  resolved(productId: number | null | undefined): IResolvedPrice | null {
    if (productId == null) return null;
    return this.byProduct.get(productId) ?? null;
  }

  candidates(productId: number | null | undefined): IPriceCandidate[] {
    // The shared empty list, not a fresh one: this feeds an @Input, and a new
    // `[]` every change-detection pass retriggers the child's ngOnChanges.
    return this.resolved(productId)?.candidates ?? NO_PRICE_CANDIDATES;
  }

  policyTrace(productId: number | null | undefined): IPricePolicyTrace | null {
    return this.resolved(productId)?.policy_trace ?? null;
  }

  /** The candidate the engine would apply, or null — never an expired one. */
  selected(productId: number | null | undefined): IPriceCandidate | null {
    return this.resolved(productId)?.selected ?? null;
  }

  /**
   * The price type a keystroke corresponds to, or null.
   *
   * Asks the engine's own candidates, so typing exactly the imported tariff is
   * labelled *Imported tariff* instead of *Manual entry* — the difference
   * between a badge that documents and a badge that guesses.
   *
   * Compared on the rendered decimal, not on a float: `12.5 === 12.50` only
   * holds after both have gone through the same normalisation.
   *
   * When several sources carry that same number, the label is the most specific
   * audience, not the first candidate in the tenant's order — the twin of
   * `_label_for` server-side, and it must stay in step with it. Otherwise a
   * tariff published to everyone can name a price the customer's own history
   * explains. Which price applies is untouched: that stays the settings' call.
   */
  priceTypeOf(productId: number | null | undefined, price: number): PriceSourceType | null {
    if (!Number.isFinite(price)) return null;
    const matches = this.candidates(productId).filter(
      (candidate) =>
        candidate.freshness !== 'expired' && samePrice(candidate.price, price)
    );
    if (!matches.length) return null;
    // `reduce` keeps the first of equally specific candidates, so a tie falls
    // back on the order the engine sent them in — the tenant's own.
    const best = matches.reduce((kept, candidate) =>
      audienceRank(candidate) < audienceRank(kept) ? candidate : kept
    );
    return PRICE_SOURCE_TO_PRICE_TYPE[best.source_kind];
  }

  /**
   * The full cascade: what a typed number is, for one line.
   *
   * Catalog and AI win first; the tail asks the engine whether the number typed
   * is one of the prices it knows, before falling back on "manual entry".
   *
   * It lives here because all three price surfaces run it and must not diverge.
   */
  priceTypeFor(
    line: { catalog_price?: number | null; ai_price?: number | null } | null | undefined,
    productId: number | null | undefined,
    price: number
  ): PriceSourceType {
    // Compared at price precision, like every other price comparison on the
    // screen: a line stores 12.340000000000002 where the input reads 12.34.
    if (line?.catalog_price != null && sameTypedPrice(price, line.catalog_price)) return 'catalog';
    if (line?.ai_price != null && sameTypedPrice(price, line.ai_price)) return 'ai';
    return this.priceTypeOf(productId, price) ?? 'override';
  }

  clear(): void {
    this.inflight?.unsubscribe();
    this.inflight = null;
    this.byProduct.clear();
    this.loadedKey = null;
  }

  /** How many open surfaces are reading this store. See `release()`. */
  private readers = 0;

  retain(): void {
    this.readers += 1;
  }

  /**
   * The memory lives until the last open surface closes.
   *
   * The review panel is a dialog over the order screen: an unconditional
   * `clear()` on destroy would wipe the candidates out from under the screen
   * still showing them. Counting here, in the one store every surface goes
   * through, keeps a new surface composition from having to handle it again.
   */
  release(): void {
    this.readers = Math.max(0, this.readers - 1);
    if (this.readers === 0) this.clear();
  }

  /** Drop the memo so the next load re-asks (after a product or customer change). */
  invalidate(): void {
    this.loadedKey = null;
    this.quotationsService.clearResolvedPricesCache();
  }
}
